/**
 * useTasksPaginated - Hook para listagem paginada de tarefas
 * 
 * Chama RPC get_tasks_paginated com filtros, paginação e ordenação server-side.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';

// Logger para depuração
const logger = createLogger('useTasksPaginated');

/**
 * Filtros da listagem de tarefas
 */
export interface TaskListFilters {
  search?: string;
  category_id?: string;
  classification_id?: string;
  phase_id?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  date_from?: string;
  date_to?: string;
  /** v1.12.0: Filtro por criador */
  created_by?: string;
  /** v1.12.0: Prazo de */
  due_date_from?: string;
  /** v1.12.0: Prazo até */
  due_date_to?: string;
  /** v1.12.0: Data conclusão de */
  completed_from?: string;
  /** v1.12.0: Data conclusão até */
  completed_to?: string;
  /** v1.15.0: Incluir tarefas concluídas (padrão: false = oculta concluídas) */
  include_completed?: boolean;
  /** v1.16.0: Filtro "Sem Prazo" — TRUE = só tarefas sem due_date; FALSE = só com due_date; undefined = sem filtro */
  due_date_is_null?: boolean;
  /** v1.17.0: Filtro "Minhas Tarefas" — UUID do usuário (responsável OU criador) */
  my_tasks_user_id?: string;
  /** v1.20.0: Lista de status a EXCLUIR (drill "Vencidas" exclui completed/cancelled) */
  status_not_in?: string[];
  /** v1.20.0: Classificação de prazo para concluídas: 'on_time' | 'late' */
  deadline_status?: 'on_time' | 'late';
  /** v1.21.0: Filtro "Sem Responsável" — TRUE = só sem responsável; FALSE = só com responsável; undefined = sem filtro */
  assigned_to_is_null?: boolean;
  /** v1.27.0: Data de solicitação (Retenção) / data de entrada (Jurídico) — de (YYYY-MM-DD) */
  request_from?: string;
  /** v1.27.0: Data de solicitação (Retenção) / data de entrada (Jurídico) — até (YYYY-MM-DD) */
  request_to?: string;
  /** v1.27.0: Faixa de período consumido (24h | 48h | 72h | 7d | 10d | 1m | 1m+) */
  period?: string;
  /** v1.27.0: Prazo processual (Jurídico) — de (YYYY-MM-DD) */
  legal_deadline_from?: string;
  /** v1.27.0: Prazo processual (Jurídico) — até (YYYY-MM-DD) */
  legal_deadline_to?: string;
  /** v1.28.0: Motivo de conclusão (drill-down do dashboard de Retenção) */
  completion_reason_id?: string;
  /** v1.28.0: Resultado do motivo — 'retained' | 'cancelled' | 'neutral' */
  outcome?: string;
  /** v1.28.0: Cliente dono do veículo vinculado à tarefa */
  customer_id?: string;
  /**
   * v1.29.0: Movimentação do dia (YYYY-MM-DD) — retorna tarefas SOLICITADAS na data
   * OU CONCLUÍDAS/CANCELADAS na data (usado no drill-down do card "Movimentação do Dia").
   */
  movement_date?: string;
}



/**
 * Item de tarefa retornado pela RPC
 */
export interface TaskListItem {
  id: string;
  number: number;
  title: string;
  category_id: string;
  category_name: string;
  category_color: string;
  classification_name: string | null;
  classification_color: string | null;
  phase_id: string;
  phase_name: string;
  phase_color: string;
  parent_task_id: string | null;
  priority: string;
  status: string;
  /** UUID do responsável — usado para contexto is_responsible no hook de permissões */
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar: string | null;
  due_date: string | null;
  created_at: string;
  is_overdue: boolean;
  subtasks_count: number;
  comments_count: number;
  total_records: number;
  /** v1.6.0: Posição da fase dentro da categoria (sort_order) */
  phase_sort_order: number;
  /** v1.6.0: Total de fases na categoria da tarefa */
  total_category_phases: number;
  /** v1.13.0: Contagem de pausas */
  pauses_count: number;
  /** v1.13.0: Tempo total de pausa em segundos */
  total_pause_duration_seconds: number;
  /** v1.13.0: Se a tarefa está pausada atualmente */
  is_paused: boolean;
  /** v1.13.0: Início da pausa ativa (null se não pausada) */
  current_pause_started_at: string | null;
  /** v1.14.0: Data/hora de início da tarefa (null se pending) */
  started_at: string | null;
  /** v1.18.0: Data/hora de conclusão (congela cronômetro) */
  completed_at: string | null;
  /** v1.18.0: Data/hora de cancelamento (congela cronômetro) */
  cancelled_at: string | null;
  /** v1.22.0: Percentual de conclusão do checklist (0-100) */
  checklist_completion_pct: number;
  /** v1.31.0: N. de protocolo (Retenção/Jurídico) */
  protocol_number?: string | null;
  /** v1.31.0: Nome do contato/cliente vinculado à tarefa (coluna Cliente) */
  contact_name?: string | null;
  /** v1.31.0: Telefone do contato vinculado (exibido abaixo do nome) */
  contact_phone?: string | null;
  /** v1.31.0: Avatar do contato vinculado (SmartAvatar) */
  contact_avatar?: string | null;
}

/**
 * Hook para listagem paginada de tarefas
 * 
 * @param filters - Filtros ativos
 * @param page - Página atual (1-indexed)
 * @param perPage - Itens por página
 * @param sortBy - Campo de ordenação
 * @param sortDir - Direção (asc/desc)
 */
export function useTasksPaginated(
  filters: TaskListFilters = {},
  page: number = 1,
  perPage: number = 25,
  sortBy: string = 'created_at',
  sortDir: string = 'desc'
) {
  const { currentTenant } = useAuth();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskType = useTaskModuleType();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['tasks-paginated', currentTenant?.id, filters, page, perPage, sortBy, sortDir, taskType],
    queryFn: async () => {
      logger.debug('Buscando tarefas paginadas', { page, perPage, filters });

      const { data, error } = await supabase.rpc('get_tasks_paginated', {
        p_page: page,
        p_per_page: perPage,
        p_search: filters.search || null,
        p_category_id: filters.category_id || null,
        p_classification_id: filters.classification_id || null,
        p_phase_id: filters.phase_id || null,
        p_status_filter: (filters.status as any) || null,
        p_priority_filter: (filters.priority as any) || null,
        p_assigned_to_filter: filters.assigned_to || null,
        p_date_from: filters.date_from || null,
        p_date_to: filters.date_to || null,
        p_sort_by: sortBy,
        p_sort_dir: sortDir,
        p_tenant_hint: currentTenant?.id || null,
        // v1.12.0: Novos filtros avançados
        p_created_by_filter: filters.created_by || null,
        p_due_date_from: filters.due_date_from || null,
        p_due_date_to: filters.due_date_to || null,
        p_completed_from: filters.completed_from || null,
        p_completed_to: filters.completed_to || null,
        // v1.15.0: Ocultar concluídas por padrão
        p_hide_completed: !(filters.include_completed),
        // v1.16.0: Filtro "Sem Prazo" / "Com Prazo"
        p_due_date_is_null:
          typeof filters.due_date_is_null === 'boolean' ? filters.due_date_is_null : null,
        // v1.17.0: Filtro "Minhas Tarefas" (responsável OU criador)
        p_my_tasks_user_id: filters.my_tasks_user_id || null,
        // v1.20.0: Drill-downs do dashboard (Vencidas / No Prazo / Atraso)
        p_status_not_in: filters.status_not_in && filters.status_not_in.length > 0 ? filters.status_not_in : null,
        p_deadline_status: filters.deadline_status || null,
        // v1.21.0: Filtro "Sem Responsável" (agrupamento por Responsável)
        p_assigned_to_is_null:
          typeof filters.assigned_to_is_null === 'boolean' ? filters.assigned_to_is_null : null,
        // Escopo do módulo corrente (Tarefas / Retenção / Jurídico)
        p_task_type: taskType,
        // v1.27.0: filtros exclusivos de Retenção/Jurídico
        p_request_from: filters.request_from || null,
        p_request_to: filters.request_to || null,
        p_period: filters.period || null,
        p_legal_deadline_from: filters.legal_deadline_from || null,
        p_legal_deadline_to: filters.legal_deadline_to || null,
        // v1.28.0: drill-down dos KPIs do dashboard de Retenção
        p_completion_reason_id: filters.completion_reason_id || null,
        p_outcome: filters.outcome || null,
        p_customer_id: filters.customer_id || null,
        // v1.29.0: movimentação do dia (solicitadas OU concluídas na data)
        p_movement_date: filters.movement_date || null,


      } as any);

      if (error) {
        logger.error('Erro ao buscar tarefas paginadas:', error);
        throw error;
      }

      return (data || []) as TaskListItem[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 15 * 1000,
  });

  // Total de registros (vem em cada row)
  const totalRecords = query.data?.[0]?.total_records ?? 0;
  const totalPages = Math.ceil(totalRecords / perPage);

  // Invalidar listagem
  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
  };

  return {
    tasks: query.data || [],
    totalRecords,
    totalPages,
    isLoading: query.isLoading,
    error: query.error,
    invalidateList,
  };
}
